import { RouterNameType } from '@/core/data/config/uiConfig.interface';
import type {
  DetailLocationDto,
  GamMapBounds,
  GeoCoords,
  MapLocationDto,
  MapMinMaxRadius,
  SearchLocationDto,
} from '@/core/data/location/location.interface';
import { gambits } from '@/core/gambits';
import { GamStoreId } from '@/views/composables/constants/main/store.constants';
import { clamp } from 'lodash';
import { defineStore } from 'pinia';
import { computed, ref, watch } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { GoogleMap } from 'vue3-google-map';
import LatLngBounds = google.maps.LatLngBounds;

export const FALLBACK_LOCATION = {
  lat: 0,
  lng: 0,
};
export const DEFAULT_ZOOM = 13;
export const DEFAULT_RADIUS = 10000;

interface CurrentLocationOptions {
  lat: number;
  lng: number;
  zoom?: number;
}

export const useMapStore = defineStore(GamStoreId.MAP, () => {
  const route = useRoute();
  const router = useRouter();

  const params = new URLSearchParams(window.location.search);
  const lat = params.get('lat');
  const lng = params.get('lng');
  const zoom = params.get('zoom');

  const initialLocation = lat && lng ? { lat: parseFloat(lat), lng: parseFloat(lng) } : FALLBACK_LOCATION;
  const currentLocation = ref<GeoCoords>(initialLocation);

  const gMap = ref<InstanceType<typeof GoogleMap>>();
  const defaultMinMax: MapMinMaxRadius = gambits.configService.getMapMinMaxRadius();
  const currentZoom = ref<number>(zoom ? parseFloat(zoom) : DEFAULT_ZOOM);
  const currentRadius = ref<number>(DEFAULT_RADIUS);
  const selectedLocationId = ref<string | null>(null);
  /** Pin that keeps the selected ring after its overlay closes, until the next map tap. */
  const ringedSpotId = ref<string | null>(null);
  const detail = ref<DetailLocationDto | null>(null);
  const mapBounds = ref<GamMapBounds>({
    north: -Infinity,
    south: Infinity,
    east: -Infinity,
    west: Infinity,
  });
  const addMode = ref<boolean>(false);
  const seekingLocation = ref<boolean>(false);
  const satelliteMode = ref<boolean>(false);
  const mapHeading = ref<number>(0);
  const bottomDrawerHeight = ref<number>(0);
  const showAllSpots = ref<boolean>(false);
  const newLocation = ref<SearchLocationDto>();
  const cursorLocation = ref<SearchLocationDto>();
  const mapProposalLocation = ref<MapLocationDto | null>(null);
  const searchPin = ref<GeoCoords | null>(null);
  const isSearchExpanded = ref(false);

  const proposalMode = ref(false);
  const checkInMode = ref(false);
  const walkSpotRole = ref<'start' | 'reveal' | null>(null);
  const walkFromReveal = ref(false);
  const pendingProposalCenter = ref<{ lat: number; lng: number } | null>(null);
  const pendingProposalRadiusM = ref<number | null>(null);
  const proposalConstraintCenter = ref<{ lat: number; lng: number } | null>(null);
  const proposalConstraintRadiusM = ref<number | null>(null);
  const seedPickerMode = ref(false);
  const selectedSeedSpot = ref<MapLocationDto | null>(null);
  const challengeUserId = ref<string | null>(null);
  const challengeUsername = ref<string | null>(null);
  const proposalClubId = ref<string | null>(null);
  const proposalClubUsername = ref<string | null>(null);
  const editGambitId = ref<string | null>(null);
  const editGambitIsChallenge = ref(false);
  const editGambitStart = ref<string | null>(null);
  const editGambitEnd = ref<string | null>(null);

  const isLocationValid = computed(() => {
    return (
      typeof currentLocation.value?.lat === 'number' &&
      currentLocation.value?.lat !== 0 &&
      typeof currentLocation.value?.lng === 'number' &&
      currentLocation.value?.lng !== 0
    );
  });

  const setCurrentLocation = ({ lat, lng, zoom }: CurrentLocationOptions) => {
    currentLocation.value = { lat, lng };
    currentZoom.value = zoom || currentZoom.value || DEFAULT_ZOOM;
  };

  const setNewRadius = (mapRadius: number): void => {
    const radiusWithBuffer = Math.round(mapRadius);
    currentRadius.value = clamp(radiusWithBuffer, 1, defaultMinMax.max);
  };

  const calculateBoundingBox = (lat: number, lon: number, radius: number): GamMapBounds => {
    const earthRadiusInMeters = 6371e3;
    const earthRadiusInDegrees = radius / earthRadiusInMeters;

    const north = lat + earthRadiusInDegrees * (180 / Math.PI);
    const south = lat - earthRadiusInDegrees * (180 / Math.PI);
    const west = lon - (earthRadiusInDegrees * (180 / Math.PI)) / Math.cos(lat * (Math.PI / 180));
    const east = lon + (earthRadiusInDegrees * (180 / Math.PI)) / Math.cos(lat * (Math.PI / 180));

    return { north, south, west, east };
  };

  const updateMapBounds = (latLngBounds?: LatLngBounds): void => {
    if (!latLngBounds) return;
    const center = latLngBounds.getCenter();
    const bounds = calculateBoundingBox(center.lat(), center.lng(), currentRadius.value);

    if (mapBounds.value.south > bounds.south) {
      mapBounds.value.south = bounds.south;
    }
    if (mapBounds.value.north < bounds.north) {
      mapBounds.value.north = bounds.north;
    }
    if (mapBounds.value.west > bounds.west) {
      mapBounds.value.west = bounds.west;
    }
    if (mapBounds.value.east < bounds.east) {
      mapBounds.value.east = bounds.east;
    }
  };

  const isOutsideBoundingBox = (latLngBounds?: LatLngBounds): boolean => {
    if (!latLngBounds) return false;
    const tempBounds = latLngBounds.toJSON();
    const { north, south, west, east } = mapBounds.value;

    return (
      (!!south && tempBounds.south < south) ||
      (!!north && tempBounds.north > north) ||
      (!!west && tempBounds.west < west) ||
      (!!east && tempBounds.east > east)
    );
  };

  const setRingedSpot = (id: string | null): void => {
    ringedSpotId.value = id;
  };

  const setSelectedLocation = (id: string | null): void => {
    if (selectedLocationId.value) {
      document.getElementById(selectedLocationId.value)?.classList.remove('selected');
    }

    selectedLocationId.value = id;
    if (id) {
      document.getElementById(id)?.classList.add('selected');
    }
  };

  const setAddMode = (value: boolean): void => {
    addMode.value = value;
  };

  const setSeekingLocation = (value: boolean): void => {
    seekingLocation.value = value;
  };

  const setNewLocation = (value?: SearchLocationDto): void => {
    newLocation.value = value;
  };

  const setCursorLocation = (value?: SearchLocationDto): void => {
    cursorLocation.value = value;
  };

  const clearAddMode = (): void => {
    setSeekingLocation(false);
    setAddMode(false);
    setNewLocation();
  };

  const VIRTUAL_LOCATION_ID = '__virtual__';

  const setMapProposalLocation = (location: MapLocationDto | null): void => {
    mapProposalLocation.value = location;
  };

  const enterProposalMode = (options?: {
    challengeUserId?: string;
    challengeUsername?: string;
    clubId?: string;
    clubUsername?: string;
    editGambitId?: string;
    editGambitIsChallenge?: boolean;
    editGambitStart?: string;
    editGambitEnd?: string;
    walkSpotRole?: 'start' | 'reveal';
    walkFromReveal?: boolean;
    proposalCenter?: { lat: number; lng: number };
    proposalRadiusM?: number;
  }) => {
    proposalMode.value = true;
    showAllSpots.value = true;
    userToggledSpots.value = true;
    challengeUserId.value = options?.challengeUserId ?? null;
    challengeUsername.value = options?.challengeUsername ?? null;
    proposalClubId.value = options?.clubId ?? null;
    proposalClubUsername.value = options?.clubUsername ?? null;
    editGambitId.value = options?.editGambitId ?? null;
    editGambitIsChallenge.value = options?.editGambitIsChallenge ?? false;
    editGambitStart.value = options?.editGambitStart ?? null;
    editGambitEnd.value = options?.editGambitEnd ?? null;
    walkSpotRole.value = options?.walkSpotRole ?? null;
    walkFromReveal.value = options?.walkFromReveal ?? false;
    pendingProposalCenter.value = options?.proposalCenter ?? null;
    pendingProposalRadiusM.value = options?.proposalRadiusM ?? null;
    proposalConstraintCenter.value = options?.proposalCenter ?? null;
    proposalConstraintRadiusM.value = options?.proposalRadiusM ?? null;
  };

  const exitProposalMode = () => {
    proposalMode.value = false;
    checkInMode.value = false;
    walkSpotRole.value = null;
    walkFromReveal.value = false;
    pendingProposalCenter.value = null;
    pendingProposalRadiusM.value = null;
    proposalConstraintCenter.value = null;
    proposalConstraintRadiusM.value = null;
    showAllSpots.value = false;
    userToggledSpots.value = false;
    challengeUserId.value = null;
    challengeUsername.value = null;
    proposalClubId.value = null;
    proposalClubUsername.value = null;
    mapProposalLocation.value = null;
    editGambitId.value = null;
    editGambitIsChallenge.value = false;
    editGambitStart.value = null;
    editGambitEnd.value = null;
  };

  const enterCheckInMode = () => {
    proposalMode.value = true;
    checkInMode.value = true;
    showAllSpots.value = true;
  };

  const exitCheckInMode = () => {
    checkInMode.value = false;
    proposalMode.value = false;
    showAllSpots.value = false;
    userToggledSpots.value = false;
    mapProposalLocation.value = null;
  };

  const enterSeedPickerMode = () => {
    seedPickerMode.value = true;
    showAllSpots.value = true;
    selectedSeedSpot.value = null;
  };

  const exitSeedPickerMode = () => {
    seedPickerMode.value = false;
    showAllSpots.value = false;
    userToggledSpots.value = false;
    selectedSeedSpot.value = null;
  };

  const selectSeedSpot = (location: MapLocationDto | null) => {
    selectedSeedSpot.value = location;
  };

  const toggleSatelliteMode = () => {
    satelliteMode.value = !satelliteMode.value;
  };

  const userToggledSpots = ref<boolean>(false);

  const toggleShowAllSpots = () => {
    showAllSpots.value = !showAllSpots.value;
    userToggledSpots.value = showAllSpots.value;
  };

  watch(
    () => route.query.spot,
    (next, prev) => {
      if (!!prev && !next) {
        setSelectedLocation(null);
      }
    },
  );

  watch(
    () => route.name,
    (name, previous) => {
      if (previous === RouterNameType.LOCATIONS && name !== RouterNameType.LOCATIONS) {
        ringedSpotId.value = null;
      }
    },
  );

  watch(
    () => route.query.side_panel,
    (sidePanel) => {
      if (sidePanel === 'search' && route.query.spot) {
        setSelectedLocation(null);
        router.replace({ ...route, query: { ...route.query, spot: undefined } });
      }
    },
  );

  return {
    gMap,
    currentRadius,
    currentLocation,
    isLocationValid,
    currentZoom,
    selectedLocationId,
    ringedSpotId,
    setRingedSpot,
    addMode,
    seekingLocation,
    newLocation,
    cursorLocation,
    satelliteMode,
    detail,
    setCurrentLocation,
    setNewRadius,
    setSelectedLocation,
    updateMapBounds,
    isOutsideBoundingBox,

    setAddMode,
    setSeekingLocation,
    setNewLocation,
    setCursorLocation,
    clearAddMode,
    toggleSatelliteMode,
    showAllSpots,
    toggleShowAllSpots,
    userToggledSpots,
    mapProposalLocation,
    setMapProposalLocation,

    VIRTUAL_LOCATION_ID,
    searchPin,
    isSearchExpanded,
    proposalMode,
    challengeUserId,
    challengeUsername,
    proposalClubId,
    proposalClubUsername,
    editGambitId,
    editGambitIsChallenge,
    editGambitStart,
    editGambitEnd,
    enterProposalMode,
    exitProposalMode,
    checkInMode,
    walkSpotRole,
    walkFromReveal,
    pendingProposalCenter,
    pendingProposalRadiusM,
    proposalConstraintCenter,
    proposalConstraintRadiusM,
    enterCheckInMode,
    exitCheckInMode,
    seedPickerMode,
    selectedSeedSpot,
    enterSeedPickerMode,
    exitSeedPickerMode,
    selectSeedSpot,
    mapHeading,
    bottomDrawerHeight,
  };
});
